import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmt = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });
};

export const generateRateConfirmationPDF = (data) => {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 40;

  // === Header bar (yellow) ===
  doc.setFillColor(249, 219, 129);
  doc.rect(0, 0, pageWidth, 90, "F");

  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.text("ROCKETSDISPATCH", margin, 42);

  doc.setFontSize(11);
  doc.setFont("helvetica", "normal");
  doc.text("Freight Brokerage Services  |  MC# 1234567  |  DOT# 9876543", margin, 60);
  doc.text("dispatch@rocketsdispatch.com  |  (855) 555-RKTS", margin, 75);

  // Right-side document title
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text("CARRIER RATE", pageWidth - margin, 42, { align: "right" });
  doc.text("CONFIRMATION", pageWidth - margin, 60, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.text(`Issued: ${new Date().toLocaleString("en-US")}`, pageWidth - margin, 78, { align: "right" });

  let y = 120;

  // Broker reference banner
  doc.setFillColor(10, 10, 10);
  doc.rect(margin, y - 18, pageWidth - margin * 2, 30, "F");
  doc.setTextColor(249, 219, 129);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text(`BROKER REFERENCE #: ${data.loadNumber || "—"}`, margin + 12, y);
  doc.setTextColor(255, 255, 255);
  doc.text(`STATUS: ${(data.status || "ASSIGNED").toUpperCase()}`, pageWidth - margin - 12, y, { align: "right" });
  y += 32;

  // === Carrier Info ===
  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("CARRIER INFORMATION", margin, y);
  doc.setDrawColor(249, 219, 129);
  doc.setLineWidth(2);
  doc.line(margin, y + 4, margin + 180, y + 4);
  y += 18;

  autoTable(doc, {
    startY: y,
    theme: "plain",
    styles: { fontSize: 10, cellPadding: 4, textColor: [20, 20, 20] },
    columnStyles: {
      0: { fontStyle: "bold", cellWidth: 130, textColor: [80, 80, 80] },
      1: { cellWidth: 170 },
      2: { fontStyle: "bold", cellWidth: 110, textColor: [80, 80, 80] },
      3: { cellWidth: 130 },
    },
    body: [
      ["Carrier Name:", data.carrierName || "—", "Carrier MC#:", data.carrierMC || "—"],
      ["Driver Name:", data.driverName || "—", "Driver Phone:", data.driverPhone || "—"],
      ["Customer (Bill To):", data.customer || "—", "Load Number:", data.loadNumber || "—"],
    ],
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 18;

  // === Routing ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("ROUTING DETAILS", margin, y);
  doc.line(margin, y + 4, margin + 160, y + 4);
  y += 18;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    headStyles: { fillColor: [10, 10, 10], textColor: [249, 219, 129], fontStyle: "bold", fontSize: 10 },
    styles: { fontSize: 9.5, cellPadding: 6, textColor: [20, 20, 20], lineColor: [200, 200, 200] },
    head: [["STOP", "LOCATION", "ADDRESS", "DATE", "APPT TIME"]],
    body: [
      ["PICKUP", data.origin || "—", data.shipperAddress || "—", data.pickupDate || "—", data.pickupTime || "—"],
      ["DELIVERY", data.destination || "—", data.consigneeAddress || "—", data.deliveryDate || "—", data.deliveryTime || "—"],
    ],
    columnStyles: { 0: { fontStyle: "bold", cellWidth: 60 } },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 18;

  // === Financials ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("FINANCIALS", margin, y);
  doc.line(margin, y + 4, margin + 110, y + 4);
  y += 14;

  const carrierPay = Number(data.carrierPay) || 0;
  const fsc = Number(data.fsc) || 0;
  const total = carrierPay + fsc;

  autoTable(doc, {
    startY: y + 6,
    theme: "grid",
    headStyles: { fillColor: [10, 10, 10], textColor: [249, 219, 129], fontStyle: "bold" },
    styles: { fontSize: 10, cellPadding: 6, textColor: [20, 20, 20] },
    head: [["LINE ITEM", "AMOUNT"]],
    body: [
      ["Carrier Linehaul Pay", fmt(carrierPay)],
      ["Fuel Surcharge (F.S.C.)", fmt(fsc)],
    ],
    foot: [["TOTAL CARRIER PAY", fmt(total)]],
    footStyles: { fillColor: [249, 219, 129], textColor: [10, 10, 10], fontStyle: "bold", fontSize: 11 },
    columnStyles: { 0: { cellWidth: 380 }, 1: { halign: "right", fontStyle: "bold" } },
    margin: { left: margin, right: margin },
  });
  y = doc.lastAutoTable.finalY + 18;

  // === Notes ===
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("BROKER NOTES / GATE INSTRUCTIONS FOR DRIVER", margin, y);
  doc.line(margin, y + 4, margin + 380, y + 4);
  y += 14;

  doc.setFillColor(255, 250, 220);
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(1.2);
  const notesText = data.notes && data.notes.trim().length > 0
    ? data.notes
    : "No special instructions provided.";
  const noteLines = doc.splitTextToSize(notesText, pageWidth - margin * 2 - 20);
  const notesBoxHeight = Math.max(50, noteLines.length * 13 + 20);
  doc.rect(margin, y, pageWidth - margin * 2, notesBoxHeight, "FD");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(20, 20, 20);
  doc.text(noteLines, margin + 10, y + 16);
  y += notesBoxHeight + 24;

  // === Agreement / Signature ===
  if (y > 680) { doc.addPage(); y = 50; }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10, 10, 10);
  doc.text("CARRIER AGREEMENT", margin, y);
  y += 12;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(60, 60, 60);
  const agreement = "By signing below or accepting this load via dispatch, the carrier agrees to transport the freight described above at the agreed rate, in accordance with the broker-carrier agreement on file. Carrier confirms valid authority, $1M auto liability, $100K cargo coverage, and on-time pickup/delivery. Any rate exceptions, layover or detention must be approved in writing by RocketsDispatch prior to incurring charges.";
  const agreeLines = doc.splitTextToSize(agreement, pageWidth - margin * 2);
  doc.text(agreeLines, margin, y);
  y += agreeLines.length * 11 + 24;

  // Signature line
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(1);
  doc.line(margin, y, margin + 220, y);
  doc.line(pageWidth - margin - 220, y, pageWidth - margin, y);
  doc.setFontSize(8.5);
  doc.setTextColor(80, 80, 80);
  doc.text("Carrier Signature / Date", margin, y + 12);
  doc.text("RocketsDispatch Broker / Date", pageWidth - margin - 220, y + 12);

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 140);
  doc.text("RocketsDispatch  •  Freight Brokerage Confirmation  •  Confidential", pageWidth / 2, 780, { align: "center" });

  const fileSafe = (data.loadNumber || "load").toString().replace(/[^a-z0-9_-]/gi, "_");
  doc.save(`RateConfirmation_${fileSafe}.pdf`);
};
